import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Select, Radio, Alert, message } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";
import TextArea from "antd/es/input/TextArea";
import Link from "antd/es/typography/Link";

const { Option } = Select;

const schema = yup.object().shape({
  title: yup.string().required("Media title is required"),
  media_type: yup.string().required("Media type is required"),
  url_data: yup.string().when("media_type", {
    is: (val) => val === "youtube" || val === "url",
    then: (schema) =>
      schema.required("URL is required").url("Please enter a valid URL"),
    otherwise: (schema) => schema.notRequired(),
  }),
  media_file: yup.mixed().when(["$isEdit", "media_type"], {
    is: (isEdit, mediaType) => {
      return !isEdit && ["video", "image", "ppt"].includes(mediaType);
    },
    then: (schema) =>
      schema
        .required("File is required")
        .test("fileType", "Unsupported file type", function (value) {
          if (!value || !value.length) return false;
          const validTypes = {
            video: ["video/mp4"],
            image: [
              "image/png",
              "image/jpeg",
              "image/jpg",
              "image/bmp",
              "image/gif",
              "image/webp",
            ],
            ppt: [
              "application/vnd.openxmlformats-officedocument.presentationml.presentation",
              "application/vnd.ms-powerpoint",
            ],
          };
          return Array.from(value).every((file) =>
            validTypes[this.parent.media_type].includes(file.type)
          );
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  status: yup.string().required("Status is required"),
});

const MediaForm = ({
  isModalOpen,
  editingMedia,
  closeModal,
  handleUpdateList,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingMedia },
    defaultValues: {
      status: "active",
    },
  });

  const watchMediaType = watch("media_type");

  const fetchMedia = async (mediaId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/media/${mediaId}`
      );
      const editFormData = {
        ...response.data.data,
        file_data:
          response.data.data.file_data &&
          JSON.parse(response.data.data.file_data),
      };
      if (response.data.data.file_data) {
        setUploadedFile(JSON.parse(response.data.data.file_data));
      }
      reset(editFormData);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (editingMedia) {
      fetchMedia(editingMedia.id);
    } else {
      setLoading(false);
    }
  }, [editingMedia]);

  const uploadFileToS3 = async (mediaTitle, files) => {
    const uploadedFiles = [];

    for (let file of files) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/media/get-signed-url`,
          {
            params: { fileName: file.name, fileType: file.type, mediaTitle },
          }
        );

        const { signedUrl, fileKey, fileUrl } = response.data;
        const uploadResponse = await fetch(signedUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) throw new Error("Failed to upload to S3");

        uploadedFiles.push({
          file_name: file.name,
          file_key: fileKey,
          file_path: fileUrl,
          file_size: (file.size / 1024).toFixed(2) + " KB",
          mime_type: file.type,
        });
      } catch (error) {
        console.error("Upload failed:", error);
        throw error;
      }
    }

    return uploadedFiles;
  };

  const onSubmitMedia = async (data) => {
    let differFormFields = {};
    if (editingMedia !== null) {
      differFormFields = {
        id: editingMedia.id,
      };
    }
    const finalFormData = {
      title: data.title.trim(),
      description: (data.description && data.description.trim()) || "",
      media_type: data.media_type,
      url_data: data.url_data || "",
      file_data: data.file_data,
      status: data.status,
      ...differFormFields,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingMedia === null
        ? `${process.env.REACT_APP_BASE_URL}/api/media/create`
        : `${process.env.REACT_APP_BASE_URL}/api/media/update`;

    try {
      if (
        ["video", "image", "ppt"].includes(data.media_type) &&
        data.media_file
      ) {
        let file_data = null;
        if (data.media_file[0] instanceof File) {
          file_data = await uploadFileToS3(
            finalFormData.title,
            editingMedia ? [data.media_file[0]] : data.media_file
          );
        }
        newFormData = {
          ...newFormData,
          file_data,
          url_data: null,
        };
      }
      if (["url", "youtube"].includes(data.media_type)) {
        newFormData = { ...newFormData, media_file: null };
      }
      if (editingMedia === null) {
        await axios.post(url, newFormData);
        // Reset form after successful submission
        reset(newFormData);
        message.success("Media created successfully");
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          reset(data);
          message.success("Media updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        // const errorField = error.response.data.field;
        console.log(errorData);
        // setShowErrors(false);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);

        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  return (
    <Modal
      title={editingMedia ? "Edit Media" : "Add New Media"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        // setEditingMedia(null);
        closeModal();
      }}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <>
              <div className="col-sm-12 mb-3">
                <Alert message={errorsMessage} type="error" closeIcon={false} />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit(onSubmitMedia)}>
            <div className="mb-3">
              <label className="form-label">
                Media Title <span className="text-danger">*</span>
              </label>
              <Controller
                name="title"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.title && (
                <div className="text-danger small">{errors.title.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => <TextArea {...field} />}
              />
              {errors.description && (
                <div className="text-danger small">
                  {errors.description.message}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">
                Media Type <span className="text-danger">*</span>
              </label>
              <Controller
                name="media_type"
                control={control}
                render={({ field }) => (
                  <Select {...field} style={{ width: "100%" }}>
                    <Option value="youtube">Youtube</Option>
                    <Option value="video">Video</Option>
                    <Option value="url">URL</Option>
                    <Option value="image">Image</Option>
                    {/* <Option value="ppt">PPT</Option> */}
                  </Select>
                )}
              />
              {errors.media_type && (
                <div className="text-danger small">
                  {errors.media_type.message}
                </div>
              )}
            </div>
            {watchMediaType && ["youtube", "url"].includes(watchMediaType) && (
              <div className="mb-3">
                <label className="form-label">
                  URL <span className="text-danger">*</span>
                </label>
                <Controller
                  name="url_data"
                  control={control}
                  render={({ field }) => <Input {...field} />}
                />
                {errors.url_data && (
                  <div className="text-danger small">
                    {errors.url_data.message}
                  </div>
                )}
              </div>
            )}
            {watchMediaType &&
              ["video", "image", "ppt"].includes(watchMediaType) && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label mb-0">
                      Upload File <span className="text-danger">*</span>
                    </label>
                    {uploadedFile && (
                      <Link
                        href={uploadedFile.file_path}
                        target="_blank"
                        className="text-decoration-none"
                      >
                        {uploadedFile.file_key.split("/").pop()}
                      </Link>
                    )}
                  </div>
                  <Controller
                    name="media_file"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="file"
                        multiple={!editingMedia}
                        accept={
                          watchMediaType === "video"
                            ? ".mp4"
                            : watchMediaType === "image"
                            ? ".png,.jpg,.jpeg,.bmp,.gif,.webp"
                            : watchMediaType === "ppt"
                            ? ".pptx,.ppt"
                            : "*"
                        }
                        onChange={(e) => {
                          field.onChange(e.target.files);
                        }}
                      />
                    )}
                  />
                  {errors.media_file && (
                    <div className="text-danger small">
                      {errors.media_file.message}
                    </div>
                  )}
                </div>
              )}

            <div className="mb-3">
              <label className="form-label me-3">Status</label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    <Radio value="active">Active</Radio>
                    <Radio value="inactive">Inactive</Radio>
                  </Radio.Group>
                )}
              />
              {errors.status && (
                <div className="text-danger small">{errors.status.message}</div>
              )}
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting}
            >
              {!submitting ? (
                editingMedia ? (
                  "Update Media"
                ) : (
                  "Add Media"
                )
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default MediaForm;
